import React from "react";
import { Button, Popconfirm, Tooltip } from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import styled from "styled-components";

import { ButtonsWrapper } from "../../../common/styledComponents";
import AddExternalTransactionButton from "./AddExternalTransactionButton";
import colors from "../../../../themes/colors";

const Container = styled.div`
  position: sticky;
  top: 17px;
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  z-index: 5;
  background-color: ${colors.white};
  padding: 15px 0;
`;

type Props = {
  addExternalTransactionClick: Function;
  onCsvImportClick: () => void;
  onDownloadClick: () => void;
  canDownload: boolean;
  hasSelectedTransactions: boolean;
  hasSelectedVerifiedTransactionsOnly?: boolean;
  verifySelectedTransactions: () => void;
  toggleShowCategorizeBatch: () => void;
  deletableSelectedCount: number;
  onBulkDeleteConfirm: () => void;
  isNested?: boolean;
};

export const TransactionsTableButtons = ({
  addExternalTransactionClick,
  onCsvImportClick,
  onDownloadClick,
  canDownload,
  hasSelectedTransactions,
  hasSelectedVerifiedTransactionsOnly,
  verifySelectedTransactions,
  toggleShowCategorizeBatch,
  deletableSelectedCount,
  onBulkDeleteConfirm,
  isNested,
}: Props) => {
  return (
    <Container>
      <div>
        <Button
          disabled={!hasSelectedTransactions}
          onClick={verifySelectedTransactions}
          size="middle"
          name="verify-selected-button"
          data-test={
            hasSelectedVerifiedTransactionsOnly
              ? "unVerifySelectedButton"
              : "verifySelectedButton"
          }
        >
          {hasSelectedVerifiedTransactionsOnly
            ? "Unverify Selected"
            : "Verify Selected"}
        </Button>
        <Button
          data-test="categorizeSelectedButton"
          disabled={!hasSelectedTransactions}
          onClick={toggleShowCategorizeBatch}
          size="middle"
          name="categorize-selected-button"
          style={{ marginLeft: "8px" }}
        >
          Categorize Selected
        </Button>
        <Tooltip title="Only external, unverified transactions can be deleted">
          <Popconfirm
            title={`Delete ${deletableSelectedCount} external transaction${
              deletableSelectedCount !== 1 ? "s" : ""
            }? This cannot be undone.`}
            onConfirm={onBulkDeleteConfirm}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            disabled={deletableSelectedCount === 0}
          >
            <Button
              data-test="bulkDeleteButton"
              danger
              disabled={deletableSelectedCount === 0}
              icon={<DeleteOutlined />}
              size="middle"
              style={{ marginLeft: "8px" }}
            >
              Delete External Selected
            </Button>
          </Popconfirm>
        </Tooltip>
      </div>
      {!isNested && (
        <ButtonsWrapper>
          <AddExternalTransactionButton
            onClick={() => {
              addExternalTransactionClick(true);
            }}
          />
          <Button
            data-test="importCsvButton"
            icon={<UploadOutlined />}
            size="middle"
            style={{ display: "flex", alignItems: "center", marginLeft: 8 }}
            onClick={onCsvImportClick}
          >
            Import CSV
          </Button>
          <Tooltip
            title={
              canDownload
                ? undefined
                : "Loading customer data, please wait a moment"
            }
          >
            <Button
              data-test="downloadTransactionsButton"
              icon={<DownloadOutlined />}
              size="middle"
              style={{ display: "flex", alignItems: "center", marginLeft: 8 }}
              onClick={onDownloadClick}
              disabled={!canDownload}
            >
              Download transactions
            </Button>
          </Tooltip>
        </ButtonsWrapper>
      )}
    </Container>
  );
};
